/**
 * **Modifier — grup & opsi** (Imron, 2026-08-04).
 *
 * ### ⭐⭐ Kenapa layar TERSENDIRI, bukan di dalam laci produk
 * Grup modifier dipakai **banyak produk**: "Tingkat pedas" melekat pada 20 menu, bukan pada satu.
 * Membuatnya dari dalam layar produk menyiratkan ia milik produk itu — dan yang terjadi berikutnya
 * adalah "Pedas" dibuat lima kali untuk lima menu, lalu menu keenam memakai yang mana pun yang
 * kebetulan ditemukan lebih dulu.
 *
 * ⇒ Pembagiannya sama persis dengan **Struktur vs Produk** yang sudah dipakai: yang ditata sekali
 * saat onboarding dipisah dari yang disunting tiap minggu. **Memilih grup mana yang berlaku** untuk
 * sebuah produk tetap keputusan produk, dan tinggal di laci produk.
 *
 * ### ⛔⛔ `minSelect` adalah KONTROL, bukan hiasan
 * "Pilih tingkat pedas" yang wajib harus **menghalangi baris tersimpan tanpa jawaban**. Server
 * menegakkannya (`validateModifierSelections`); layar cuma membuat penolakan itu tak perlu terjadi
 * di depan tamu. ⚠️ `maxSelect = 0` berarti **tanpa batas** — bukan "tak boleh memilih apa pun".
 *
 * ### ⛔ Teks bebas TAK PERNAH berharga (§5d)
 * "sausnya dipisah" mengubah cara membuat, bukan yang dibayar. Kalau ia bisa berharga, harga sebuah
 * baris jadi bergantung pada kalimat yang diketik kasir — dan tak ada satu pun laporan yang bisa
 * menjelaskannya kemudian.
 */
import { Alert, Card, Form, Input, InputNumber, Select, Space, Switch, Table, Tag, Typography, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from '../api/client';
import { canWrite } from '../api/session';
import { CrudPanel } from '../components/CrudPanel';

interface GroupRow {
  readonly id: string;
  readonly code: string;
  readonly name: string;
  readonly min_select: number;
  readonly max_select: number;
  readonly allow_free_text: boolean;
  readonly is_active: boolean;
}
interface OptionRow {
  readonly id: string;
  readonly group_id: string;
  readonly code: string;
  readonly name: string;
  readonly kind: string;
  readonly price_delta: string;
  readonly is_active: boolean;
}

/** ⛔ Nilai kontrak — `free` · `priced` · `priced_stock` (§5d). */
const KINDS = ['free', 'priced', 'priced_stock'] as const;

export function Modifiers() {
  const { t } = useTranslation();
  const boleh = canWrite();
  const [groupId, setGroupId] = useState<string | null>(null);
  const [groups, setGroups] = useState<readonly GroupRow[]>([]);
  const [tick, setTick] = useState(0);

  const muatGrup = useCallback(async () => {
    const g = await api.listMaster<GroupRow>('modifier-groups', { includeInactive: 'true' });
    setGroups(g);
    setGroupId((cur) => cur ?? g[0]?.id ?? null);
    return g;
  }, []);

  useEffect(() => { void muatGrup().catch(() => undefined); }, [muatGrup, tick]);

  const kolomGrup: ColumnsType<GroupRow> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 160,
      render: (v: string) => <b data-testid={`mgrup-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      /*
       * ⭐ Aturan pilih tampil DI TABEL. "Wajib atau tidak" adalah satu-satunya hal yang menentukan
       * apakah kasir bisa menyimpan baris — dan menyembunyikannya di dalam laci berarti orang
       * menyetelnya sekali lalu tak pernah memeriksanya lagi.
       */
      title: t('modifier.rule'),
      key: 'aturan',
      width: 240,
      render: (_: unknown, g: GroupRow) => (
        <Space size={4} wrap>
          {g.min_select > 0
            ? <Tag color="red" data-testid={`mgrup-wajib-${g.code}`}>{t('modifier.required', { n: g.min_select })}</Tag>
            : <Tag data-testid={`mgrup-opsional-${g.code}`}>{t('modifier.optional')}</Tag>}
          {/* ⚠️ 0 = TANPA BATAS, bukan "tak boleh memilih". */}
          <Tag>{g.max_select === 0 ? t('modifier.maxNone') : t('modifier.max', { n: g.max_select })}</Tag>
          {g.allow_free_text && <Tag color="blue">{t('modifier.freeText')}</Tag>}
        </Space>
      ),
    },
    {
      title: '',
      dataIndex: 'is_active',
      width: 96,
      render: (v: boolean) =>
        v ? <Tag color="green">{t('outlets.active')}</Tag> : <Tag>{t('outlets.inactive')}</Tag>,
    },
  ];

  const grupTerpilih = groups.find((g) => g.id === groupId) ?? null;

  return (
    <Space orientation="vertical" size="middle" style={{ width: '100%' }} data-testid="modifiers">
      {/* ⚠️ Disebut supaya yang mencari "pasang modifier ke produk" tak menyimpulkan fiturnya hilang. */}
      <Alert type="info" showIcon data-testid="mod-catatan" title={t('modifier.attachHint')} />

      <CrudPanel<GroupRow>
        testId="mgrup"
        title={t('modifier.groups')}
        subtitle={t('modifier.groupsSubtitle')}
        canWrite={boleh}
        newLabel={t('modifier.newGroup')}
        columns={kolomGrup}
        fetchList={muatGrup}
        searchIn={(g) => `${g.code} ${g.name}`}
        emptyText={t('modifier.noGroup')}
        reloadToken={tick}
        onSelect={(g) => { setGroupId(g.id); }}
        {...(groupId === null ? {} : { selectedId: groupId })}
        renderFields={(_f, editing) => (
          <>
            {editing === null && (
              <Form.Item name="code" label={t('master.code')}
                         rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}
                         tooltip={t('structure.codeFixed')}>
                <Input maxLength={40} data-testid="mgrup-kode" />
              </Form.Item>
            )}
            <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 2 }]}>
              <Input maxLength={120} data-testid="mgrup-nama" />
            </Form.Item>
            <Space align="start" wrap>
              {/*
                ⛔⛔ `minSelect ≥ 1` = WAJIB DIJAWAB. Ia menghalangi baris tersimpan tanpa jawaban —
                server yang menegakkannya, layar cuma mencegah penolakan terjadi di depan tamu.
              */}
              <Form.Item name="minSelect" label={t('modifier.minSelect')} tooltip={t('modifier.minHint')}
                         initialValue={0}>
                <InputNumber min={0} max={20} data-testid="mgrup-min" />
              </Form.Item>
              {/* ⚠️ 0 = tanpa batas. Disebut di `extra` supaya tak dibaca sebagai "nol pilihan". */}
              <Form.Item name="maxSelect" label={t('modifier.maxSelect')} tooltip={t('modifier.maxHint')}
                         initialValue={0}>
                <InputNumber min={0} max={20} data-testid="mgrup-max" />
              </Form.Item>
            </Space>
            <Form.Item name="allowFreeText" label={t('modifier.freeText')} valuePropName="checked"
                       tooltip={t('modifier.freeTextHint')}>
              <Switch data-testid="mgrup-teks-bebas"
                      checkedChildren={t('common.on')} unCheckedChildren={t('common.off')} />
            </Form.Item>
            {editing !== null && (
              <Form.Item name="isActive" label={t('outlets.status')} valuePropName="checked">
                <Switch data-testid="mgrup-aktif"
                        checkedChildren={t('outlets.active')} unCheckedChildren={t('outlets.inactive')} />
              </Form.Item>
            )}
          </>
        )}
        toFormValues={(g) => ({
          name: g.name, minSelect: g.min_select, maxSelect: g.max_select,
          allowFreeText: g.allow_free_text, isActive: g.is_active,
        })}
        onCreate={async (v) => {
          await api.createMaster('modifier-groups', {
            code: String(v['code']).toUpperCase(),
            name: String(v['name']),
            minSelect: Number(v['minSelect'] ?? 0),
            maxSelect: Number(v['maxSelect'] ?? 0),
            allowFreeText: v['allowFreeText'] === true,
          });
        }}
        onUpdate={async (id, v, lama) => {
          await api.updateMaster('modifier-groups', id, {
            name: v['name'],
            minSelect: Number(v['minSelect'] ?? 0),
            maxSelect: Number(v['maxSelect'] ?? 0),
            allowFreeText: v['allowFreeText'] === true,
          });
          if (v['isActive'] !== lama.is_active) {
            await api.setStatus('modifier-groups', id, v['isActive'] === true);
          }
        }}
      />

      {/* ── opsi milik grup terpilih ─────────────────────────────────────── */}
      {grupTerpilih === null ? (
        <Alert type="info" showIcon data-testid="mopsi-pilih-grup" title={t('modifier.pickGroup')} />
      ) : (
        <Card
          title={t('modifier.optionsOf', { name: grupTerpilih.name })}
          data-testid="mopsi-kartu"
        >
          {/*
            ⛔ Grup TANPA opsi tak berguna — dan lebih buruk: kalau ia wajib, kasir mustahil
            menyimpan baris apa pun yang memakainya. Itu harus terlihat di sini, bukan ditemukan
            saat jam sibuk.
          */}
          {grupTerpilih.min_select > 0 && (
            <Alert type="warning" showIcon style={{ marginBottom: 12 }}
                   data-testid="mopsi-wajib-ingat" title={t('modifier.requiredWarning')} />
          )}
          <OptionList groupId={grupTerpilih.id} canWrite={boleh} onChanged={() => { setTick((n) => n + 1); }} />
        </Card>
      )}

      <Typography.Text type="secondary">{t('modifier.freeTextNeverPriced')}</Typography.Text>
    </Space>
  );
}

/** Opsi satu grup. Dipisah supaya `CrudPanel` punya `fetchList` yang benar-benar berubah per grup. */
function OptionList(p: { groupId: string; canWrite: boolean; onChanged: () => void }) {
  const { t } = useTranslation();

  const kolom: ColumnsType<OptionRow> = [
    {
      title: t('master.code'),
      dataIndex: 'code',
      width: 140,
      render: (v: string) => <b data-testid={`mopsi-${v}`}>{v}</b>,
    },
    { title: t('master.name'), dataIndex: 'name' },
    {
      title: t('modifier.kind'),
      dataIndex: 'kind',
      width: 200,
      render: (v: string) => (
        <Tag color={v === 'free' ? 'default' : v === 'priced_stock' ? 'purple' : 'blue'}
             data-testid={`mopsi-jenis-${v}`}>
          {t(`modifier.kinds.${v}`)}
        </Tag>
      ),
    },
    {
      title: t('modifier.priceDelta'),
      dataIndex: 'price_delta',
      width: 140,
      align: 'right',
      /* ⚠️ Ditampilkan apa adanya dari server — layar tak pernah menghitung uang sendiri. */
      render: (v: string, r: OptionRow) => (r.kind === 'free' ? '—' : v.split('.')[0]),
    },
  ];

  return (
    <CrudPanel<OptionRow>
      testId="mopsi"
      title={t('modifier.options')}
      canWrite={p.canWrite}
      newLabel={t('modifier.newOption')}
      columns={kolom}
      fetchList={() => api.listMaster<OptionRow>('modifier-options', { group_id: p.groupId })}
      searchIn={(o) => `${o.code} ${o.name}`}
      emptyText={t('modifier.noOption')}
      renderFields={(_f, editing) => (
        <>
          {editing === null && (
            <Form.Item name="code" label={t('master.code')}
                       rules={[{ required: true, pattern: /^[A-Za-z0-9_-]+$/, message: t('structure.codeRule') }]}>
              <Input maxLength={40} data-testid="mopsi-kode" />
            </Form.Item>
          )}
          <Form.Item name="name" label={t('master.name')} rules={[{ required: true, min: 1 }]}>
            <Input maxLength={120} data-testid="mopsi-nama" />
          </Form.Item>
          {editing === null && (
            <Form.Item name="kind" label={t('modifier.kind')} rules={[{ required: true }]}
                       initialValue="free" tooltip={t('modifier.kindHint')}>
              <Select
                data-testid="mopsi-jenis"
                options={KINDS.map((k) => ({ value: k, label: t(`modifier.kinds.${k}`) }))}
              />
            </Form.Item>
          )}
          {/*
            ⚠️ Tambahan harga hanya berarti untuk `priced`/`priced_stock`. Untuk `free` ia diabaikan
            server — dan dibiarkan terlihat supaya orang tak mengira harganya hilang.
          */}
          <Form.Item name="priceDelta" label={t('modifier.priceDelta')} tooltip={t('modifier.priceHint')}
                     initialValue={0}>
            <InputNumber min={0} step={500} data-testid="mopsi-harga" />
          </Form.Item>
        </>
      )}
      toFormValues={(o) => ({
        name: o.name,
        priceDelta: Number(o.price_delta.split('.')[0] ?? '0'),
      })}
      onCreate={async (v) => {
        await api.createMaster('modifier-options', {
          groupId: p.groupId,
          code: String(v['code']).toUpperCase(),
          name: String(v['name']),
          kind: String(v['kind'] ?? 'free'),
          priceDelta: `${String(Number(v['priceDelta'] ?? 0))}.0000`,
        });
        p.onChanged();
      }}
      onUpdate={async (id, v) => {
        await api.updateMaster('modifier-options', id, {
          name: v['name'],
          priceDelta: `${String(Number(v['priceDelta'] ?? 0))}.0000`,
        });
        p.onChanged();
      }}
    />
  );
}
